import { axiosAdminApiClient } from "../index";
import { handleError } from "../../utils/errors";

class SetsService {
  // Get all sets with filtering
  getAllSets = async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.search) queryParams.append("search", params.search);
      if (params.status) queryParams.append("status", params.status);
      if (params.excludeStatus) queryParams.append("excludeStatus", params.excludeStatus);
      if (params.featured !== undefined)
        queryParams.append("featured", params.featured);
      if (params.minPrice) queryParams.append("minPrice", params.minPrice);
      if (params.maxPrice) queryParams.append("maxPrice", params.maxPrice);

      const response = await axiosAdminApiClient.get(
        `sets?${queryParams.toString()}`,
        {
          withCredentials: true,
        }
      );

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting sets");
    }
  };

  getFeaturedSets = async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.excludeStatus) queryParams.append("excludeStatus", params.excludeStatus);

      queryParams.append("featured", true);

      const response = await axiosAdminApiClient.get(
        `sets?${queryParams.toString()}`,
        {
          withCredentials: true,
        }
      );

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting featured sets");
    }
  };

  // Get single set by ID (public route with limited data)
  getPublicSetById = async (id) => {
    try {
      const response = await axiosAdminApiClient.get(`sets/public/${id}`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting public set");
    }
  };

  // Get single set by ID (admin/authenticated route with full data)
  getSetById = async (id) => {
    try {
      const response = await axiosAdminApiClient.get(`sets/${id}`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting set");
    }
  };

  // Get purchased set by ID (authenticated user only)
  getMySetById = async (id) => {
    try {
      const response = await axiosAdminApiClient.get(`sets/my/${id}`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting purchased set");
    }
  };

  // Create new set (superAdmin only)
  createSet = async (setData) => {
    try {
      const response = await axiosAdminApiClient.post("sets", setData, {
        withCredentials: true,
      });

      if (response.status !== 201) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating set");
    }
  };

  // Update existing set (superAdmin only)
  updateSet = async (id, setData) => {
    try {
      const response = await axiosAdminApiClient.put(`sets/${id}`, setData, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating set");
    }
  };

  // Delete set (superAdmin only)
  deleteSet = async (id) => {
    try {
      const response = await axiosAdminApiClient.delete(`sets/${id}`, {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting set");
    }
  };

  // Get sets statistics (admin/superAdmin only)
  getSetsStatistics = async () => {
    try {
      const response = await axiosAdminApiClient.get("sets/statistics", {
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting sets statistics");
    }
  };

  // Export all sets
  exportSets = async () => {
    try {
      const response = await axiosAdminApiClient.get("sets/admin/export", {
        responseType: 'blob', // Important for file download
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sets_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: "Export je uspešno završen" };
    } catch (err) {
      throw handleError(err, "Error occurred while exporting sets");
    }
  };

  // Import sets from file
  importSets = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosAdminApiClient.post("sets/admin/import", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while importing sets");
    }
  };
}

const setsService = new SetsService();

export default setsService;
